import { defineStore } from 'pinia'
import { authService } from '@/services/authService'
import { scheduleTokenRefresh, cancelTokenRefresh, initSessionRefresh } from '@/services/api'
import { profileService } from '@/services/profileService'
import { devRoleLabel, devPermissionsForRole } from '@/config/permissions'
import { i18n } from '@/i18n'
import { flushDrafts } from '@/pwa/runtime'
import { countPending, clearDrafts } from '@/pwa/drafts'
import { syncOutbox, initOutbox } from '@/pwa/outbox'

const lsParse = (key, fallback) => {
  try {
    const val = localStorage.getItem(key)
    if (!val || val === 'undefined' || val === 'null') return fallback
    return JSON.parse(val)
  } catch {
    localStorage.removeItem(key)
    return fallback
  }
}

let _refreshing = null
let _lastRefresh = 0
let _initDone = false

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: lsParse('user', null),
    loading: false,
    error: null
  }),

  getters: {
    isAuthenticated: (state) => !!state.user && !!localStorage.getItem('access_token'),
    // Effective per-module permissions for the logged-in user, as computed
    // server-side from their (admin-defined) role. See config/permissions.js.
    permissions: (state) => state.user?.permissions || {},
    roleLabel: (state) => state.user?.role_name || (state.user?.role ? devRoleLabel(state.user.role) : ''),
    farmId: (state) => state.user?.farm_id
  },

  actions: {
    async login(credentials) {
      this.loading = true
      this.error = null
      try {
        const data = await authService.login(credentials)
        this._applySession(data)
        return data
      } catch (err) {
        this.error = err.message
        throw err
      } finally {
        this.loading = false
      }
    },

    /**
     * Dev-only mockup session — no backend call, no real token. Lets `/preview/:role`
     * render the app (Dashboard, Sidebar, permission filtering) without a running
     * backend. Wired up only behind `import.meta.env.DEV` in router/index.js, so
     * it never ships in a production build.
     */
    loginMock(role) {
      const t = i18n.global.t
      this._applySession({
        user: {
          id: 'mock-user',
          farm_id: 'mock-farm',
          email: `${role}@demo.smartfarm.vn`,
          name: i18n.global.te(`preview.demoNames.${role}`) ? t(`preview.demoNames.${role}`) : t('preview.demoNames.default'),
          role,
          role_name: devRoleLabel(role),
          permissions: devPermissionsForRole(role)
        },
        tokens: { access_token: 'mock-token', refresh_token: 'mock-refresh' }
      })
    },

    setName(name) {
      this.user = { ...this.user, name }
      localStorage.setItem('user', JSON.stringify(this.user))
    },

    /** Merge the fields shown in the app shell from a fresh /me response. */
    setProfile(u) {
      this.user = { ...this.user, name: u.name, phone: u.phone, avatar_url: u.avatar_url, job_title: u.job_title }
      localStorage.setItem('user', JSON.stringify(this.user))
    },

    /**
     * Re-read /me and merge the fresh permissions/role/profile into the session.
     * Skips when logged out, offline, mock session, or already in flight. Never
     * touches drafts. A /me for a different account id is ignored (the draft
     * owner key stays tied to the logged-in session). Errors are swallowed:
     * 401 is handled by the api interceptor, anything else keeps stored data.
     */
    async refreshMe() {
      const token = localStorage.getItem('access_token')
      if (!this.user || !token || token === 'mock-token') return false
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
      if (_refreshing) return _refreshing
      _refreshing = (async () => {
        try {
          const u = await profileService.get()
          if (!u || typeof u !== 'object') return false
          if (u.id && this.user?.id && u.id !== this.user.id) return false
          const next = { ...this.user }
          for (const k of ['name', 'phone', 'avatar_url', 'job_title', 'role', 'role_id', 'role_name']) {
            if (u[k] !== undefined && u[k] !== null) next[k] = u[k]
          }
          if (u.permissions && typeof u.permissions === 'object') next.permissions = u.permissions
          this.user = next
          localStorage.setItem('user', JSON.stringify(next))
          _lastRefresh = Date.now()
          return true
        } catch {
          return false
        } finally {
          _refreshing = null
        }
      })()
      return _refreshing
    },

    // Returns false (and keeps the session) while unsent drafts exist unless the
    // caller already obtained the user's consent via { discardDrafts: true }.
    async logout({ discardDrafts = false } = {}) {
      await flushDrafts()
      // Send what is queued before deciding whether anything would be lost.
      await syncOutbox().catch(() => {})
      if (!discardDrafts && await countPending() > 0) return false
      await clearDrafts()
      cancelTokenRefresh()
      this.user = null
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      return true
    },

    /** Start a session from a login/register response. */
    applySession(data) {
      this._applySession(data)
    },

    _applySession(data) {
      this.user = data.user
      localStorage.setItem('user', JSON.stringify(data.user))
      localStorage.setItem('access_token', data.tokens.access_token)
      if (data.tokens.refresh_token) {
        localStorage.setItem('refresh_token', data.tokens.refresh_token)
      }
      scheduleTokenRefresh()
    }
  }
})

/**
 * Wire the automatic /me refreshes (call once after the app is mounted):
 * on start (if logged in), when the tab becomes visible after > 60 s, and on
 * the 'sf:permissions-stale' event that api.js emits on a 403 (it debounces
 * to once per 30 s, so there is no loop).
 */
export function initAuthRefresh() {
  if (_initDone || typeof document === 'undefined') return
  _initDone = true
  const store = useAuthStore()
  _lastRefresh = Date.now()
  initSessionRefresh()
  initOutbox()
  store.refreshMe()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - _lastRefresh > 60000) {
      _lastRefresh = Date.now()
      store.refreshMe()
    }
  })
  window.addEventListener('sf:permissions-stale', () => { store.refreshMe() })
}
